// src/components/home/Overview.tsx — the built-in home view, built from blocks (Task M; prototype §19, §28b, §12).
//
// What shows, and in what order, is the user's layout: their own, else their role's (an admin arranges it), else
// every block they may see. Each block is a stat tile or a short list computed on the server by the service that
// owns the figure; clicking it goes to the rows behind the number. The moving-in / moving-out block opens a
// slide-over with what is still to do on that flat's checklist.
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, ExternalLink, StickyNote } from 'lucide-react';
import { useCan } from '../../hooks/usePermission';
import { StatTile } from '../charts/StatTile';
import { Card, PageHeader } from '../ui';
import { Drawer } from '../building/bits';
import { DrillDrawer } from '../director';
import { ArrangeDialog } from './ArrangeDialog';
import { directorApi, fmtMoney, type BlockData, type BlockItem, type Drill } from '../../lib/director';
import { PageLoader } from '../BrandLoader';

function dayChip(days: number | null | undefined) {
  if (days === null || days === undefined) return { t: 'No date', c: 'bg-ink-100 text-ink-500' };
  if (days < 0) return { t: `${Math.abs(days)}d ago`, c: 'bg-red-50 text-red-600' };
  if (days === 0) return { t: 'Today', c: 'bg-amber-50 text-amber-700' };
  return { t: `in ${days}d`, c: days <= 7 ? 'bg-amber-50 text-amber-700' : 'bg-ink-100 text-ink-600' };
}

function MovesBlock({ block, onOpen }: { block: BlockData; onOpen: (i: BlockItem) => void }) {
  const items = block.items ?? [];
  return (
    <Card title={block.title} subtitle="Next 14 days, with what is still to do" actions={block.drill?.path ? <Link to={block.drill.path} className="text-[12px] font-medium text-bronze-600 hover:underline">Changeovers</Link> : undefined}>
      {!items.length ? <p className="py-6 text-center text-[13px] text-ink-400">Nobody moving in or out in the next fortnight.</p> : (
        <ul className="-mx-2 divide-y divide-ink-100">{items.map((i) => {
          const chip = dayChip(i.days);
          return (
            <li key={i.id}><button type="button" onClick={() => onOpen(i)} className="flex w-full items-center justify-between gap-3 rounded-xl px-2 py-2.5 text-left transition hover:bg-bronze-50/60">
              <span className="min-w-0"><span className="block truncate text-[13.5px] font-medium text-ink-900">{i.title}</span><span className="block truncate text-[12px] text-ink-500">{i.sub} · {i.outstanding?.length ?? 0} to do</span></span>
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${chip.c}`}>{chip.t}</span>
            </button></li>
          );
        })}</ul>
      )}
    </Card>
  );
}

function ListBlock({ block }: { block: BlockData }) {
  const items = block.items ?? [];
  return (
    <Card title={block.title} subtitle={block.hint ?? undefined} actions={block.drill?.path ? <Link to={block.drill.path} className="text-[12px] font-medium text-bronze-600 hover:underline">All</Link> : undefined}>
      {!items.length ? <p className="py-6 text-center text-[13px] text-ink-400">Nothing here.</p> : (
        <ul className="-mx-2 divide-y divide-ink-100">{items.map((i) => (
          <li key={i.id}><Link to={i.url ?? block.drill?.path ?? '#'} className="flex items-center justify-between gap-3 rounded-lg px-2 py-2 transition hover:bg-ink-50">
            <span className="min-w-0"><span className="block truncate text-[13.5px] font-medium text-ink-900">{i.title}</span>{i.sub && <span className="block truncate text-[12px] text-ink-500">{i.sub}</span>}</span>
            <ArrowRight className="h-4 w-4 shrink-0 text-ink-300" />
          </Link></li>
        ))}</ul>
      )}
    </Card>
  );
}

function NoteBlock({ block }: { block: BlockData }) {
  return (
    <div className="surface flex flex-col gap-2 p-4">
      <div className="flex items-center gap-2 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-ink-400"><StickyNote className="h-3.5 w-3.5" /> Note</div>
      <div className="text-[13px] font-semibold text-ink-900">{block.title}</div>
      {block.text && <p className="whitespace-pre-line text-[13px] text-ink-600">{block.text}</p>}
      {block.link && <Link to={block.link} className="mt-auto inline-flex items-center gap-1 text-[12.5px] font-medium text-bronze-700 hover:underline">Open <ExternalLink className="h-3 w-3" /></Link>}
    </div>
  );
}

const toneOf = (t: BlockData['tone']) => (t === 'critical' ? 'critical' : t === 'warning' ? 'warning' : t === 'good' ? 'good' : undefined);
const valueOf = (b: BlockData) => (b.value === null || b.value === undefined ? '—' : b.format === 'money' ? fmtMoney(b.value, b.currency, true) : b.format === 'percent' ? `${b.value}%` : String(b.value));

export function Overview() {
  const navigate = useNavigate();
  const canArrange = useCan('dashboard.update');
  const [arranging, setArranging] = useState(false);
  const [moving, setMoving] = useState<BlockItem | null>(null);
  const [drill, setDrill] = useState<{ drill: Drill; title: string } | null>(null);
  const q = useQuery({ queryKey: ['home-blocks'], queryFn: directorApi.blocks });
  const blocks = q.data?.blocks ?? [];
  const stats = blocks.filter((b) => b.kind === 'stat');
  const others = blocks.filter((b) => b.kind !== 'stat');
  const open = (b: BlockData) => { if (b.drill?.path) navigate(b.drill.path); else if (b.drill?.reportKey) setDrill({ drill: b.drill, title: b.title }); };

  return (
    <div className="space-y-2.5">
      {q.isLoading && <PageLoader />}
      {q.data && !blocks.length && <p className="py-10 text-center text-[13px] text-ink-500">Your home screen is empty. {canArrange && <button type="button" onClick={() => setArranging(true)} className="font-medium text-bronze-600 hover:underline">Arrange blocks →</button>}</p>}
      {stats.length > 0 && (
        <div className="tile-row">
          {stats.map((b) => <StatTile key={b.key} label={b.title} value={valueOf(b)} hint={b.hint ?? undefined} status={toneOf(b.tone)} onClick={b.drill ? () => open(b) : undefined} />)}
        </div>
      )}
      {others.length > 0 && (
        <div className="grid grid-cols-1 gap-2.5 lg:grid-cols-2">
          {others.map((b) => <div key={b.key} className="min-w-0">{b.kind === 'moves' ? <MovesBlock block={b} onOpen={setMoving} /> : b.kind === 'note' ? <NoteBlock block={b} /> : <ListBlock block={b} />}</div>)}
        </div>
      )}
      {/* "Arrange blocks" sits in the page header like every other page's buttons (Invoices, Banking). */}
      <PageHeader title="Dashboard" menu={canArrange && q.data ? [{ label: 'Arrange blocks', icon: 'LayoutList', onClick: () => setArranging(true) }] : undefined} />

      {arranging && <ArrangeDialog onClose={() => setArranging(false)} />}
      {drill && <DrillDrawer drill={drill.drill} title={drill.title} onClose={() => setDrill(null)} />}
      {moving && (
        <Drawer title={moving.title} subtitle={`${moving.sub ?? ''}${moving.date ? ` · ${moving.date}` : ''}`} onClose={() => setMoving(null)}
          footer={moving.url && <Link to={moving.url} className="inline-flex h-8 items-center gap-1 rounded-full bg-bronze-500 px-3.5 text-[12.5px] font-medium text-on-primary transition hover:bg-bronze-600">Open the checklist <ArrowRight className="h-3.5 w-3.5" /></Link>}>
          <p className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-ink-400">Still to do · {moving.outstanding?.length ?? 0}</p>
          {!moving.outstanding?.length ? <p className="py-6 text-[13px] text-ink-500">Every step is ticked — the closing email is ready to go.</p> : (
            <ul className="mt-2 space-y-1.5">{moving.outstanding.map((o, i) => (
              <li key={i} className="flex items-start gap-2 rounded-lg border border-ink-200 px-3 py-2 text-[13px] text-ink-700"><span className="mt-1 h-2 w-2 shrink-0 rounded-full border border-ink-300" />{o}</li>
            ))}</ul>
          )}
        </Drawer>
      )}
    </div>
  );
}
